import { FadeTransition, FlexCenter } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useCallback, useEffect } from "react";
import translate from "src/lib/translate";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import LoginForm from "src/ui/components/LoginForm/LoginForm";
import ResetPasswordForm from "src/ui/components/ResetPasswordForm/ResetPasswordForm";
import type { SignupCustomStepProps } from "src/ui/components/SignupCustomContent/RenderSignupCustomStep";
import { SignupCustomStepType } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import RegisterForm from "../../RegisterForm/RegisterForm";

const PositionHelper = styled.div`
  label: PositionHelper;
  position: relative;
  padding: 2rem var(--space-md);
  height: 100%;
`;

const SignupCustomStepAuthenticationContent: FC<SignupCustomStepProps> = ({
  step,
  onStepCompleted
}) => {
  const authStage = useUserAuthStage();
  const [, { campaignFunnelKey, showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [view, setView] = React.useState<"login" | "register" | "reset-pw">(
    "register"
  );

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      if (loading) {
        showSaving({
          text: translate("savingIndicator.info", { context: "authentication" })
        });
      }
    },
    [showSaving]
  );

  useEffect(() => {
    if (authStage === "fullAuth") {
      void onStepCompleted(step);
    }
  }, [authStage]);

  const handleSuccess = async () => onStepCompleted(step);

  const handleError = useCallback(() => {
    hideSaving();
  }, [hideSaving]);

  return (
    <FadeTransition show fitParent>
      <PositionHelper>
        <FlexCenter>
          {view === "register" && (
            <RegisterForm
              loginLink={() => setView("login")}
              scopeKey={campaignFunnelKey}
              onSuccess={handleSuccess}
              onLoadingChange={handleLoadingChange}
              onError={handleError}
            />
          )}
          {view === "login" && (
            <LoginForm
              registerLink={() => setView("register")}
              resetPasswordLink={() => setView("reset-pw")}
              onSuccess={handleSuccess}
              onLoadingChange={handleLoadingChange}
              onError={handleError}
            />
          )}
          {view === "reset-pw" && (
            <ResetPasswordForm
              loginLink={() => setView("login")}
              onSuccess={handleSuccess}
              onLoadingChange={handleLoadingChange}
              onError={handleError}
            />
          )}
        </FlexCenter>
      </PositionHelper>
    </FadeTransition>
  );
};

const SignupCustomStepAuthentication: FC<SignupCustomStepProps> = React.memo(
  (props) => {
    const isAuthPage = props.step.type === SignupCustomStepType.Authentication;
    return isAuthPage ? (
      <SignupCustomStepAuthenticationContent
        onStepCompleted={props.onStepCompleted}
        step={props.step}
      />
    ) : (
      <></>
    );
  }
);

export default SignupCustomStepAuthentication;
